import { useRef, useEffect, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";

interface WebTerminalProps {
  cwd?: string;
  className?: string;
  onReady?: () => void;
}

export function WebTerminal({ cwd, className, onReady }: WebTerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const [status, setStatus] = useState<"connecting" | "connected" | "disconnected" | "error">("connecting");

  useEffect(() => {
    if (!containerRef.current) return;

    // Create terminal
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace",
      theme: {
        background: "#0d1117",
        foreground: "#e6edf3",
        cursor: "#58a6ff",
        selectionBackground: "#264f78",
        black: "#0d1117",
        red: "#ff7b72",
        green: "#3fb950",
        yellow: "#d29922",
        blue: "#58a6ff",
        magenta: "#bc8cff",
        cyan: "#39d353",
        white: "#e6edf3",
        brightBlack: "#484f58",
        brightRed: "#ffa198",
        brightGreen: "#56d364",
        brightYellow: "#e3b341",
        brightBlue: "#79c0ff",
        brightMagenta: "#d2a8ff",
        brightCyan: "#56d364",
        brightWhite: "#ffffff",
      },
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(containerRef.current);
    fitAddon.fit();

    termRef.current = term;
    fitRef.current = fitAddon;

    // Fetch terminal config from gateway
    const jwtToken = localStorage.getItem("infra_token") || "";
    fetch(`${API_BASE}/v1/terminal/config`, {
      headers: { authorization: `Bearer ${jwtToken}` },
    })
      .then(r => r.json())
      .then((config: any) => {
        if (!config.available || !config.ws_url) {
          term.write("\r\n\x1b[33mTerminal not configured. Check Settings → Integrations.\x1b[0m\r\n");
          setStatus("error");
          return;
        }

        const params = new URLSearchParams();
        if (config.token) params.set("token", config.token);
        if (cwd) params.set("cwd", cwd);
        params.set("cols", String(term.cols));
        params.set("rows", String(term.rows));

        const wsUrl = `${config.ws_url}?${params}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      term.focus();
      onReady?.();
    };

    ws.onmessage = (event) => {
      term.write(typeof event.data === "string" ? event.data : new Uint8Array(event.data));
    };

    ws.onclose = () => {
      setStatus("disconnected");
      term.write("\r\n\x1b[90m[Terminal disconnected]\x1b[0m\r\n");
    };

    ws.onerror = () => {
      setStatus("error");
      term.write("\r\n\x1b[31m[Connection error]\x1b[0m\r\n");
    };

    // Terminal → WebSocket
    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(data);
    });

    // Handle resize
    const handleResize = () => {
      fitAddon.fit();
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
      })
      .catch(() => {
        term.write("\r\n\x1b[31m[Failed to fetch terminal config]\x1b[0m\r\n");
        setStatus("error");
      });

    // Cleanup
    return () => {
      if (wsRef.current) wsRef.current.close();
      term.dispose();
      termRef.current = null;
      wsRef.current = null;
      fitRef.current = null;
    };
  }, [cwd]);

  return (
    <div className={`relative ${className || ""}`}>
      {/* Status indicator */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${
          status === "connected" ? "bg-green-400" :
          status === "connecting" ? "bg-yellow-400 animate-pulse" :
          status === "error" ? "bg-red-400" : "bg-gray-400"
        }`} />
        <span className="text-[10px] text-gray-500 font-mono">{status}</span>
      </div>
      {/* Terminal container */}
      <div ref={containerRef} className="w-full h-full min-h-[200px]" />
    </div>
  );
}
